"use client"

import * as React from "react"
import { Dialog as DialogPrimitive } from "@base-ui/react/dialog"
import { Button } from "@/primitives/button"
import { Drawer, DrawerContent } from "@/primitives/drawer"
import { useOverlayForm } from "@/design-system/use-overlay-form.ts"
import { cn } from "@/primitives/utils"
import { XIcon } from "lucide-react"

/*
 * beacon: a dialog chooses its form as it opens (`useOverlayForm`) and keeps it while open. On the
 * desk it is the centred dialog below. On `compact` it is a bottom sheet: a Drawer root, whose
 * Trigger, Close, Title and Description are Dialog's own, so a caller's parts work in either form
 * and callers don't change. The sheet is as tall as its content (`size` sm, md, lg) or the full
 * height (`xl`, and `position="top"`); it ignores the width. `fullScreen` makes it the whole
 * screen instead, for a task that takes it over (the command palette's Search, the Composer). Its
 * footer sticks to its bottom with the actions sharing the width, above the keyboard.
 */
const DialogFormContext = React.createContext<"dialog" | "sheet">("dialog")

function Dialog({ ...props }: DialogPrimitive.Root.Props) {
  const { docked, onOpenChange } = useOverlayForm(props)
  return (
    <DialogFormContext value={docked ? "sheet" : "dialog"}>
      {docked ? (
        // The sheet closes for two reasons a dialog has no word for (a swipe, Android's Back),
        // which reach the caller's handler as Dialog's details: callers read `open`, not why.
        <Drawer
          data-slot="dialog"
          {...(props as React.ComponentProps<typeof Drawer>)}
          onOpenChange={onOpenChange as React.ComponentProps<typeof Drawer>["onOpenChange"]}
        />
      ) : (
        <DialogPrimitive.Root data-slot="dialog" {...props} onOpenChange={onOpenChange} />
      )}
    </DialogFormContext>
  )
}

function DialogTrigger({ ...props }: DialogPrimitive.Trigger.Props) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />
}

function DialogPortal({ ...props }: DialogPrimitive.Portal.Props) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />
}

function DialogClose({ ...props }: DialogPrimitive.Close.Props) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />
}

function DialogOverlay({ className, ...props }: DialogPrimitive.Backdrop.Props) {
  return (
    <DialogPrimitive.Backdrop
      data-slot="dialog-overlay"
      className={cn(
        // beacon: the scrim token, with no blur, fading in over 150ms.
        "fixed inset-0 isolate z-50 bg-scrim duration-150 data-open:animate-in data-open:fade-in-0 data-closed:animate-out data-closed:duration-100 data-closed:fade-out-0",
        className,
      )}
      {...props}
    />
  )
}

/*
 * beacon: dialogs come in four widths, sm 24rem · md 28rem (the default) · lg 36rem · xl 64rem, and
 * two positions: centred, or `top` at 12% of the viewport for the composers and the command palette,
 * so a list growing beneath the input never makes the dialog jump. Content sits in DialogHeader,
 * DialogBody and DialogFooter, which carry the padding; the surface itself has none.
 */
const dialogSizes = {
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-xl",
  xl: "max-w-5xl",
} as const

const dialogPositions = {
  center: "top-1/2 max-h-[calc(100dvh-2rem)] -translate-y-1/2",
  top: "top-[12vh] max-h-[calc(88dvh-1rem)] translate-y-0",
} as const

function DialogContent({
  className,
  children,
  showCloseButton = true,
  size = "md",
  position = "center",
  fullScreen = false,
  ...props
}: DialogPrimitive.Popup.Props & {
  showCloseButton?: boolean
  size?: keyof typeof dialogSizes
  position?: keyof typeof dialogPositions
  /** beacon: on `compact`, the whole screen rather than a sheet. The desk ignores it. */
  fullScreen?: boolean
}) {
  // beacon: on `compact`, a bottom sheet, whose own width and position outrank the caller's.
  if (React.use(DialogFormContext) === "sheet")
    return (
      <DrawerContent
        data-slot="dialog-content"
        data-size={size}
        data-position={position}
        size={fullScreen ? "screen" : size === "xl" || position === "top" ? "full" : "auto"}
        showCloseButton={showCloseButton}
        {...({ className, ...props } as React.ComponentProps<typeof DrawerContent>)}
      >
        {children}
      </DrawerContent>
    )
  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Popup
        data-slot="dialog-content"
        data-size={size}
        data-position={position}
        className={cn(
          // beacon: the floating surface, lifted by the dialog shadow (its hairline replaces the
          // ring), entering with a fade and a slight zoom over 150ms.
          "fixed left-1/2 z-50 grid w-[calc(100%-2rem)] -translate-x-1/2 gap-0 overflow-y-auto rounded-xl bg-popover p-0 text-ui text-popover-foreground shadow-dialog duration-150 ease-snappy outline-none data-open:animate-in data-open:fade-in-0 data-open:zoom-in-[0.98] data-closed:animate-out data-closed:duration-100 data-closed:fade-out-0 data-closed:zoom-out-[0.98]",
          dialogSizes[size],
          dialogPositions[position],
          className,
        )}
        {...props}
      >
        {children}
        {showCloseButton && (
          <DialogPrimitive.Close
            data-slot="dialog-close"
            render={<Button variant="ghost" className="absolute top-3 right-3" size="icon-sm" />}
          >
            <XIcon />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Popup>
    </DialogPortal>
  )
}

function DialogHeader({ className, ...props }: React.ComponentProps<"div">) {
  const sheet = React.use(DialogFormContext) === "sheet"
  return (
    <div
      data-slot="dialog-header"
      // beacon: 20px in from the edges, clear of the close button on the right; in a sheet, close
      // under its handle.
      className={cn("flex flex-col gap-1 px-5 pt-5 pb-3 pr-12", sheet && "pt-1", className)}
      {...props}
    />
  )
}

/** beacon: a dialog's content between its header and footer, on the same 20px gutters. */
function DialogBody({ className, ...props }: React.ComponentProps<"div">) {
  return <div data-slot="dialog-body" className={cn("px-5 pb-4", className)} {...props} />
}

function DialogFooter({
  className,
  showCloseButton = false,
  children,
  ...props
}: React.ComponentProps<"div"> & {
  showCloseButton?: boolean
}) {
  const sheet = React.use(DialogFormContext) === "sheet"
  return (
    <div
      data-slot="dialog-footer"
      // beacon: actions at the end, on a hairline-topped strip. In a sheet the strip sticks to its
      // bottom, above the keyboard, and the paired actions share its width; a leading group (the
      // wrapper around a Remove before Cancel and Save) takes a line of its own above them, its
      // button full width.
      className={cn(
        "flex items-center justify-end gap-2 border-t border-border-subtle px-5 py-3",
        sheet &&
          "sticky bottom-0 mt-auto flex-wrap bg-popover *:flex-1 *:[div]:basis-full *:[div]:*:w-full",
        className,
      )}
      {...props}
    >
      {children}
      {showCloseButton && (
        <DialogPrimitive.Close render={<Button variant="outline" />}>Close</DialogPrimitive.Close>
      )}
    </div>
  )
}

function DialogTitle({ className, ...props }: DialogPrimitive.Title.Props) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      // beacon: a title in the type scale.
      className={cn("font-heading text-title font-semibold", className)}
      {...props}
    />
  )
}

function DialogDescription({ className, ...props }: DialogPrimitive.Description.Props) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn(
        // beacon: UI text in the secondary tier.
        "text-ui text-muted-foreground *:[a]:text-brand-text *:[a]:underline-offset-3 *:[a]:hover:underline",
        className,
      )}
      {...props}
    />
  )
}

export {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
}
